/**
 * Data layer for `creative-hosting`. Thin typed fetch wrappers over
 * /api/v1/creative-hosting.
 *
 * Raw `fetch()` is confined to this file per the dash D6 contract rule.
 *
 * Matches the backend router in apps/api/src/routes/creative-hosting.ts:
 *   POST   /                         -> create
 *   GET    /campaign/:campaignId     -> list by campaign
 *   GET    /:id                      -> read one
 *   PUT    /:id                      -> patch
 *   DELETE /:id                      -> hard-delete
 */

export const CREATIVE_TYPES = [
	"banner",
	"video",
	"html",
	"native",
	"email",
	"lp",
	"drive_link",
	"other",
] as const;

export type CreativeType = (typeof CREATIVE_TYPES)[number];

export interface CreativeHostingRow {
	id: string;
	campaign_id: string;
	label: string;
	type: CreativeType;
	storage_key: string;
	size_bytes: number;
	mime: string;
	created_at: number;
	updated_at: number;
}

export interface ListCreativeHostingResponse {
	campaign_id: string;
	count: number;
	items: CreativeHostingRow[];
}

export interface CreateCreativeHostingBody {
	campaignId: string;
	label: string;
	type: CreativeType;
	storageKey: string;
	sizeBytes: number;
	mime: string;
}

const BASE = "/api/v1/creative-hosting";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listByCampaign(
	campaignId: string,
): Promise<ListCreativeHostingResponse> {
	const res = await fetch(
		`${BASE}/campaign/${encodeURIComponent(campaignId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListCreativeHostingResponse>(res);
}

export async function create(
	body: CreateCreativeHostingBody,
): Promise<CreativeHostingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreativeHostingRow>(res);
}

export async function remove(id: string): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

export const creativeHostingQueryKeys = {
	all: ["creative-hosting"] as const,
	list: (campaignId: string) =>
		[...creativeHostingQueryKeys.all, "campaign", campaignId] as const,
	detail: (id: string) => [...creativeHostingQueryKeys.all, id] as const,
};

/**
 * Build a shareable preview URL for a creative. `storage_key` is opaque, so
 * we embed it in a read-through endpoint hosted alongside the API. The dash
 * itself does not serve the asset — this is just the link users copy.
 */
export function buildPreviewUrl(row: CreativeHostingRow): string {
	const origin =
		typeof window !== "undefined" && window.location
			? window.location.origin
			: "";
	return `${origin}/api/v1/creative-hosting/${encodeURIComponent(
		row.id,
	)}/preview`;
}

/** Format bytes as a short human string. Pure, safe in tests and SSR. */
export function formatBytes(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB", "TB"];
	let n = bytes / 1024;
	let i = 0;
	while (n >= 1024 && i < units.length - 1) {
		n /= 1024;
		i++;
	}
	const rounded = n >= 10 ? Math.round(n) : Math.round(n * 10) / 10;
	return `${rounded} ${units[i]}`;
}

/** Hard upload cap enforced in the browser. 50 MB. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
